import Button from "common/components/Button/Button";

import { ObservationGroup } from "types/ObservationGroup";
import { Link } from "react-router-dom";
import useAuth from "common/hooks/useAuth";
import { useTranslation } from "react-i18next";
import AnimalListOverview from "components/Item/AnimalListOverview";
import { useMemo } from "react";
import { TableCellsIcon } from "@heroicons/react/24/solid";
import TooltipWrapper from "common/components/TooltipWrapper/TooltipWrapper";
import { ObservationGrid } from "types/ObservationGrid";
import _ from "lodash";

function ObservationGroupItem({ item, onDelete, onNavigate }: any) {
  const { user: currentUser } = useAuth();
  const canSeeDetailsFromItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.["observation-group"]?.read)) ??
    false;
  const canEditItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.["observation-group"]
          ?.update)) ??
    false;
  const canDeleteItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.["observation-group"]
          ?.delete)) ??
    false;

  const { name, animals } = item as ObservationGroup;

  const editLink = `/observation-groups/edit/${item.id}`;

  const { t } = useTranslation();

  const observationGrids = useMemo(() => {
    const items = [
      ...(animals?.flatMap((a) => a.species?.observationGrids) ?? []),
      ...(animals?.flatMap(
        (a) => a.species?.genus?.superfamily?.observationGrids
      ) ?? []),
    ];
    const itemsIds = _.uniq(items?.filter((i) => i).map((i) => i?.id));
    return itemsIds?.map((i) => items.find((a) => a.id === i));
  }, [animals]);

  return (
    <div className="flex px-4 py-4 mt-2 w-full relative bg-white cursor-pointer rounded-lg border border-slate-150 transition-all">
      <Link
        to={editLink}
        className={
          !canSeeDetailsFromItem
            ? "pointer-events-none flex flex-1"
            : "flex flex-1"
        }
      >
        <div className="flex flex-col flex-1 justify-between pl-4 pr-6">
          <div className="flex mt-4 mb-2 gap-3">
            <div className="flex-1">
              <h4 className="text-xl font-bold text-primary leading-4 mb-2">
                {name}
              </h4>
            </div>
            {Boolean(animals?.length) && (
              <AnimalListOverview animals={animals} />
            )}
          </div>
        </div>
      </Link>

      <div className="flex flex-col justify-between items-end text-xs lg:border-l pl-4 lg:min-w-[22em] border-slate-150">
        <TooltipWrapper
          className="mr-1"
          content={
            observationGrids?.length > 0 ? (
              <div className="flex flex-col flex-wrap gap-1">
                {observationGrids?.map((grid: ObservationGrid | undefined) =>
                  grid ? <span className="font-bold">{grid.name}</span> : null
                )}
              </div>
            ) : null
          }
        >
          <div className="flex items-center gap-1 text-sm">
            <TableCellsIcon className="w-3 h-3" />
            <span className="font-bold">
              {t("forms.observationGridsLength", {
                count: observationGrids?.length || 0,
              })}
            </span>
          </div>
        </TooltipWrapper>
        <div className="flex w-full items-center justify-end text-slate-500 gap-1 mt-4">
          <TooltipWrapper content={<span>{t("actions.delete")}</span>}>
            <Button
              type="error-line"
              icon="TrashIcon"
              compact
              onClick={() => onDelete(item.id)}
              disabled={!canDeleteItem}
              confirmMessage={t("actions.confirmDelete")}
            />
          </TooltipWrapper>

          <Button
            title={t("actions.edit")}
            type="warning-line"
            icon="PencilSquareIcon"
            onClick={() => onNavigate(item.id)}
            disabled={!canEditItem}
          />
        </div>
      </div>
    </div>
  );
}

export default ObservationGroupItem;
